import type { SilkCreatorProfile, SilkWork } from '@/api/types'

/** 是否为系统自动写入的脱敏手机号昵称 */
function isMaskedPhoneNickname(nickname: string): boolean {
  return /^\d{3}\*{4}\d{4}$/.test(nickname.trim())
}

export function phoneSuffix(phone?: string | null): string {
  if (!phone) return ''
  const digits = phone.replace(/\D/g, '')
  return digits.length >= 4 ? digits.slice(-4) : digits
}

export function defaultUserDisplayName(phone?: string | null): string {
  const suffix = phoneSuffix(phone)
  return suffix ? `用户${suffix}` : '用户'
}

/** 未设置昵称时的展示名：用户 + 手机后四位；忽略系统自动写入的脱敏手机号昵称 */
export function resolveUserDisplayName(input?: {
  display_name?: string
  nickname?: string | null
  username?: string
  phone?: string | null
} | null): string {
  if (!input) return '用户'
  if (input.display_name?.trim()) return input.display_name.trim()
  const nick = input.nickname?.trim() ?? ''
  if (nick && !isMaskedPhoneNickname(nick)) return nick
  if (input.phone) return defaultUserDisplayName(input.phone)
  const masked = nick.match(/^\d{3}\*{4}(\d{4})$/)
  if (masked) return `用户${masked[1]}`
  return nick || input.username || '用户'
}

export function creatorDisplayName(
  profile: Pick<SilkCreatorProfile, 'display_name' | 'nickname' | 'username'>,
): string {
  return resolveUserDisplayName(profile)
}

export function creatorPublicHandle(
  profile: Pick<SilkCreatorProfile, 'public_handle' | 'username'>,
): string {
  return profile.public_handle || profile.username
}

/** 对外分享与导航：统一使用 id 路由，避免 username 泄漏 */
export function creatorPublicPath(profile: { id: number }): string {
  return `/creators/${profile.id}`
}

export function creatorPathFromWork(work: Pick<SilkWork, 'user_id'>): string {
  return `/creators/${work.user_id}`
}

export function userPublicHandle(user: { id: number; username?: string }): string | null {
  if (!user.username) return null
  if (/^u\d{7,15}$/.test(user.username)) return `uid${user.id}`
  return user.username
}

/** 裸「用户」/「创作者 #id」视为无效展示名，继续回退 */
function isWeakCreatorLabel(name?: string | null): boolean {
  const t = name?.trim() ?? ''
  if (!t) return true
  if (t === '用户') return true
  if (/^用户\d{0,4}$/.test(t)) return true
  if (/^创作者\s*#?\d+$/.test(t)) return true
  return false
}

export function creatorLabelFromWork(
  work: Pick<SilkWork, 'author_display_name' | 'author_nickname' | 'author_username' | 'user_id'>,
  fallback: string,
): string {
  const display = work.author_display_name?.trim()
  if (display && !isWeakCreatorLabel(display)) return display
  const nick = work.author_nickname?.trim()
  if (nick && !isWeakCreatorLabel(nick) && !isMaskedPhoneNickname(nick)) return nick
  const fromProfile = resolveUserDisplayName({
    nickname: work.author_nickname,
    username: work.author_username,
  })
  if (fromProfile && !isWeakCreatorLabel(fromProfile)) return fromProfile
  const username = work.author_username?.trim()
  if (username && !/^u\d{7,15}$/.test(username)) {
    return username.replace(/^creator_/, '')
  }
  if (!isWeakCreatorLabel(fallback)) return fallback
  return work.user_id ? `创作者 #${work.user_id}` : '创作者'
}
